/**
 * useSystemVersion.js
 * v3.21-1795 · Canonical Hub version SSOT hook.
 *
 * Single runtime source of truth for the Hub build version.  Every
 * surface that renders a version tag (sidebar, footer, SMS Dashboard,
 * Client Workspace, login page, …) should consume this hook so a
 * single bump in `backend/main.py` + `backend/server.py` +
 * `frontend/public/version.json` propagates through every display
 * automatically — no per-component hardcoded fallbacks.
 *
 * Load order (identical to the sidebar/footer implementation that
 * already worked in Production, now factored into a shared hook):
 *   1. GET `${REACT_APP_BACKEND_URL}/api/version` → uses the
 *      backend's SYSTEM_VERSION SSOT (the same string set by
 *      backend/main.py + backend/server.py).
 *   2. Fallback to `/version.json` (built from
 *      frontend/public/version.json).
 *   3. Final fallback to empty string ("") so no stale hardcoded
 *      literal can ever surface again.
 *
 * A module-level cache and in-flight promise are shared across
 * mounts so the fetch happens at most once per SPA session — every
 * component that calls the hook after the first mount receives the
 * cached value synchronously.
 */
import { useEffect, useState } from 'react';

let _cache = null;
let _promise = null;

async function _loadVersion() {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (backendUrl) {
      const apiRes = await fetch(`${backendUrl}/api/version`);
      if (apiRes.ok) {
        const data = await apiRes.json();
        if (data && data.version) return data.version;
      }
    }
    const res = await fetch('/version.json');
    if (res.ok) {
      const data = await res.json();
      if (data && data.version) return data.version;
      if (data && (data.major || data.minor || data.patch)) {
        return `v${data.major}.${data.minor}-${String(data.patch).padStart(2, '0')}`;
      }
    }
  } catch (_) { /* fall through */ }
  return '';
}

export function useSystemVersion() {
  const [version, setVersion] = useState(_cache || '');
  useEffect(() => {
    if (_cache) { setVersion(_cache); return; }
    if (!_promise) {
      _promise = _loadVersion().then((v) => { _cache = v || ''; return _cache; });
    }
    let mounted = true;
    _promise.then((v) => { if (mounted) setVersion(v); });
    return () => { mounted = false; };
  }, []);
  return version;
}

export default useSystemVersion;
